"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Checkbox,
  Panel,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { CheckList } from "@/components/tools/research-writing/check-list";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  buildLimitationsDocument,
  emptyEntries,
  type LimitationEntry,
  type LimitationFormat,
  type LimitationId,
  type LimitationScope,
  type LimitationsInput,
  limitationChecks,
  limitationsWords,
  selectedTypes,
  typesForScope,
} from "@/lib/tool-kit/research-writing/limitations";

/** English strings. A translated content file overrides these through `ui`. */
export const LIMITATIONS_PARAGRAPH_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  scopeHeading: "Your study",
  scopeLabel: "Type of study",
  scopes: {
    quantitative: "Quantitative",
    qualitative: "Qualitative",
    all: "Show every type",
  },
  formatLabel: "Write it as",
  formats: {
    paragraph: "One paragraph",
    separate: "A paragraph for each limitation",
    bullets: "A bulleted list",
  },
  pickHeading: "Which limitations apply?",
  pickNote:
    "Tick only what is true of your study. For each, add what is specific to it: the tool supplies a lead-in and one general sentence about what this kind of limitation can do. Edit the general sentence to fit.",
  detailLabel: "What exactly (optional)",
  mitigationLabel: "What you did about it (a sentence, optional)",
  impactLabel: "What it means for your findings (a sentence, optional)",
  limitationLabel: "Limitation {n}",
  despiteLabel: "What still holds despite the limitations (optional)",
  despitePlaceholder: "the pattern was the same in both samples",
  despiteHint:
    "Becomes “Despite these limitations, …”. Only claim what the evidence supports.",
  typeLabels: {
    sampleSize: "A small sample",
    selection: "Volunteer or convenience sampling",
    observational: "Observational design, no random assignment",
    noComparison: "No comparison or control group",
    measurement: "Self-report or imperfect measures",
    attrition: "Attrition or missing data",
    confounding: "Unmeasured confounding variables",
    analysis: "Many tests or assumptions only partly met",
    transferability: "Transferability of the findings",
    researcher: "The researcher’s role and assumptions",
    sampleQual: "A purposive sample and saturation",
    timeframe: "A short time frame or follow-up",
    secondary: "Existing data collected for another purpose",
    language: "Language or cultural context of data collection",
  },
  detailPlaceholders: {
    sampleSize: "n = 42 students",
    selection: "participants were recruited from two classes",
    observational: "students chose whether to attend",
    noComparison: "one group measured before and after",
    measurement: "attendance was self-reported",
    attrition: "18% did not complete the follow-up",
    confounding: "prior achievement was not recorded",
    analysis: "twelve outcomes were tested",
    transferability: "one school in one district",
    researcher: "the interviewer was also the teacher",
    sampleQual: "twelve interviews",
    timeframe: "one semester",
    secondary: "records were collected for administration",
    language: "interviews were translated from Spanish",
  },
  phrases: {
    sampleSize: "the size of the sample",
    selection: "how participants were selected",
    observational: "the observational design",
    noComparison: "the absence of a comparison group",
    measurement: "how the variables were measured",
    attrition: "attrition and missing data",
    confounding: "variables that were not measured",
    analysis: "the analysis",
    transferability: "how far the findings transfer to other settings",
    researcher: "the researcher’s own role in the study",
    sampleQual: "the sample and the point at which data collection stopped",
    timeframe: "the length of the study period",
    secondary: "the use of data collected for another purpose",
    language: "the language and cultural context of the data",
  },
  consequences: {
    sampleSize:
      "A small sample reduces statistical power and the precision of estimates, so smaller effects may have gone undetected and the estimates found may vary more from sample to sample.",
    selection:
      "People who volunteer or are easiest to reach can differ from the wider population, so the results may reflect who took part rather than the population as a whole.",
    observational:
      "Without random assignment, the groups may differ in ways that were not measured, so the associations found cannot establish that one variable caused another.",
    noComparison:
      "Without a comparison group, change over time cannot be separated from what would have happened anyway.",
    measurement:
      "Self-reported data can be affected by recall and by a wish to give acceptable answers, and any measure that is not fully valid or reliable adds error.",
    attrition:
      "If participants who dropped out or did not respond differ from those who remained, the results may be biased, and the sample analysed is smaller than the one recruited.",
    confounding:
      "Variables that were not measured may account for part of the associations observed, even after adjusting for those that were.",
    analysis:
      "Running many comparisons raises the chance of finding an apparent effect by chance, and assumptions that are only approximately met can affect the accuracy of the estimates.",
    transferability:
      "Qualitative findings are rooted in a particular context, so readers must judge how far they apply elsewhere; they are not statistically generalizable.",
    researcher:
      "A researcher’s position and assumptions shape the data collected and how it is interpreted, which reflexivity can make visible but cannot remove.",
    sampleQual:
      "A small purposive sample can give depth but may leave out perspectives that were not represented, and judging when no new themes were emerging is an interpretive decision.",
    timeframe:
      "A short study period may miss effects that take longer to appear or that fade over time.",
    secondary:
      "Data collected for another purpose may not include the best measures of the variables of interest, and the researchers had no control over how they were collected.",
    language:
      "Data gathered in one language or cultural context, or translated, may lose nuance in meaning.",
  },
  resultsHeading: "Checks",
  statWords: "Words",
  statLimitations: "Limitations",
  checksLabel: "Checklist",
  checkStates: { done: "Done", todo: "To do", note: "Check" },
  checkText: {
    chosen_done: "At least one limitation is chosen.",
    chosen_todo: "Tick the limitations that apply to your study.",
    detail_done: "Every limitation says what exactly is limited.",
    detail_note:
      "Limitation {items} has no detail. A specific limitation is more credible than a generic one.",
    mitigation_done: "Every limitation says what you did about it.",
    mitigation_note:
      "Limitation {items} has no mitigation. If you did nothing, that is fine to say, but if you did something, say it.",
    impact_done: "Every limitation says what it means for the findings.",
    impact_note:
      "Limitation {items} does not say how it affects the findings. Say which way it could bias the result, and how much, if you can.",
    despite_done: "The paragraph ends on what still holds.",
    despite_note: "Consider ending on what the findings can still support.",
  },
  fileName: "limitations-paragraph",
  document: {
    heading: "Your limitations paragraph",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Nothing left to write.",
  },
  defaultTitle: "Limitations",
  subtitle: "Limitations paragraph",
  heading: "Limitations",
  tip: "The general sentences are written for each type of limitation, not for your study. Read each one and edit it until it is true of your study.",
  leadIns: [
    "One limitation of this study concerns",
    "A second limitation concerns",
    "A third limitation concerns",
    "A fourth limitation concerns",
    "Another limitation concerns",
  ],
  despiteLead: "Despite these limitations,",
  prompts: {
    none: "Tick at least one limitation above to write the paragraph.",
    despite: "",
  },
  example: {
    scope: "quantitative",
    despite: "the association was similar in each of the two cohorts",
    sampleSize: {
      detail: "n = 120 students at one university",
      mitigation:
        "We report confidence intervals so that readers can judge the precision of each estimate.",
      impact: "The findings should therefore be read as preliminary.",
    },
    observational: {
      detail: "students chose whether to attend tutoring",
      mitigation: "We adjusted for prior grades and course load.",
      impact:
        "Students who sought tutoring may have been more motivated, which would make the association look stronger than it is.",
    },
    measurement: {
      detail: "hours of study were self-reported",
      mitigation: "",
      impact: "",
    },
  },
} as const;

const EMPTY: LimitationsInput = {
  scope: "quantitative",
  entries: emptyEntries(),
  despite: "",
  format: "paragraph",
};

const SCOPES: readonly LimitationScope[] = [
  "quantitative",
  "qualitative",
  "all",
];
const FORMATS: readonly LimitationFormat[] = [
  "paragraph",
  "separate",
  "bullets",
];

type FieldKey = "detail" | "mitigation" | "impact";

export default function LimitationsParagraphGeneratorWidget() {
  const strings = useToolUi(LIMITATIONS_PARAGRAPH_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<LimitationsInput>(EMPTY);
  const labels = useMemo(() => strings, [strings]);
  const doc = useMemo(
    () => buildLimitationsDocument(input, labels, locale),
    [input, labels, locale]
  );
  const documentLabels = useMemo(() => strings.document, [strings]);
  const picked = selectedTypes(input);
  const words = limitationsWords(input, labels, locale);
  const checkText = strings.checkText as Record<string, string>;
  const checks = limitationChecks(input).map((check) => ({
    key: check.id,
    state: check.state,
    text: t(checkText[`${check.id}_${check.state}`] ?? "", {
      items: (check.items ?? []).join(", "),
    }),
  }));

  const setEntry = (id: LimitationId, patch: Partial<LimitationEntry>) =>
    setInput((previous) => ({
      ...previous,
      entries: {
        ...previous.entries,
        [id]: { ...previous.entries[id], ...patch },
      },
    }));
  const loadExample = () => {
    const e = strings.example;
    const entries = emptyEntries();
    for (const id of ["sampleSize", "observational", "measurement"] as const) {
      entries[id] = { on: true, ...e[id] };
    }
    setInput({ ...EMPTY, scope: "quantitative", entries, despite: e.despite });
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={loadExample}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.scopeHeading}>
        <div className="grid gap-6 sm:grid-cols-2">
          <PillGroup
            label={strings.scopeLabel}
            name="lp-scope"
            onChange={(scope) => setInput((p) => ({ ...p, scope }))}
            options={SCOPES.map((value) => ({
              value,
              label: strings.scopes[value],
            }))}
            value={input.scope}
          />
          <PillGroup
            label={strings.formatLabel}
            name="lp-format"
            onChange={(format) => setInput((p) => ({ ...p, format }))}
            options={FORMATS.map((value) => ({
              value,
              label: strings.formats[value],
            }))}
            value={input.format}
          />
        </div>
      </Panel>

      <Panel title={strings.pickHeading}>
        <p className={HINT}>{strings.pickNote}</p>
        {typesForScope(input.scope).map((id) => {
          const entry = input.entries[id];
          const number = picked.indexOf(id) + 1;
          return (
            <div className="space-y-3" key={id}>
              <Checkbox
                checked={entry.on}
                label={strings.typeLabels[id]}
                onChange={(on) => setEntry(id, { on })}
              />
              {entry.on ? (
                <fieldset className="space-y-3 rounded-lg border border-border p-4">
                  <legend className="px-1 font-medium text-foreground text-sm">
                    {t(strings.limitationLabel, { n: number })}
                  </legend>
                  <TextField
                    id={`lp-${id}-detail`}
                    label={strings.detailLabel}
                    onChange={(value) => setEntry(id, { detail: value })}
                    placeholder={strings.detailPlaceholders[id]}
                    value={entry.detail}
                  />
                  {(["mitigation", "impact"] as FieldKey[]).map((field) => (
                    <TextAreaField
                      id={`lp-${id}-${field}`}
                      key={field}
                      label={
                        field === "mitigation"
                          ? strings.mitigationLabel
                          : strings.impactLabel
                      }
                      onChange={(value) => setEntry(id, { [field]: value })}
                      prose
                      rows={2}
                      value={entry[field]}
                    />
                  ))}
                </fieldset>
              ) : null}
            </div>
          );
        })}
        <TextAreaField
          hint={strings.despiteHint}
          id="lp-despite"
          label={strings.despiteLabel}
          onChange={(despite) => setInput((p) => ({ ...p, despite }))}
          placeholder={strings.despitePlaceholder}
          prose
          rows={2}
          value={input.despite}
        />
      </Panel>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        <StatGrid>
          <StatCard emphasis label={strings.statWords} value={fmt(words)} />
          <StatCard
            label={strings.statLimitations}
            value={fmt(picked.length)}
          />
        </StatGrid>
        <CheckList
          items={checks}
          label={strings.checksLabel}
          stateLabels={strings.checkStates}
        />
      </section>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="lp"
        labels={documentLabels}
      />
    </div>
  );
}
